import { Quill } from 'react-quill';
import { useSnackbar } from 'notistack';

import EditorToolbarStyle from './EditorToolbarStyle';
import { IconRedo, IconUndo } from 'src/libraries/icons';

// ----------------------------------------------------------------------

const FONT_FAMILY = ['Arial', 'Tahoma', 'Georgia', 'Impact', 'Verdana'];

const FONT_SIZE = [
  '8px',
  '9px',
  '10px',
  '12px',
  '14px',
  '16px',
  '20px',
  '24px',
  '32px',
  '42px',
  '54px',
  '68px',
  '84px',
  '98px',
];
const HEADINGS = ['Heading 1', 'Heading 2', 'Heading 3', 'Heading 4', 'Heading 5', 'Heading 6'];

export function undoChange() {
  // @ts-ignore
  this.quill.history.undo();
}
export function redoChange() {
  // @ts-ignore
  this.quill.history.redo();
}

const Size = Quill.import('attributors/style/size');
Size.whitelist = FONT_SIZE;
Quill.register(Size, true);

const Font = Quill.import('attributors/style/font');
Font.whitelist = FONT_FAMILY;
Quill.register(Font, true);

let linkErrorMessage: string = '';

var Link = Quill.import('formats/link');
Link.PROTOCOL_WHITELIST = ['http', 'https', 'mailto', 'tel', 'radar', 'rdar', 'smb', 'sms'];

class CustomLinkSanitizer extends Link {
  static sanitize(url: string) {
    linkErrorMessage = '';
    
    const sanitizedUrl = super.sanitize(url);

    if (!sanitizedUrl || sanitizedUrl === 'about:blank') {
      return sanitizedUrl;
    }

    const hasWhitelistedProtocol = this.PROTOCOL_WHITELIST.some(function(protocol: string) {
      return sanitizedUrl.startsWith(protocol)
    })

    if (hasWhitelistedProtocol) {
      return sanitizedUrl;
    };

    linkErrorMessage = 'Please enter a valid URL address';
    return sanitizedUrl;
  }
}

Quill.register(CustomLinkSanitizer, true);

export const formats = [
  'bold',
  'italic',
  'underline',
  'script', // not the "<script>" tag, but rather "<sub>" and "<sup>"
  'link',
];

type EditorToolbarProps = {
  id: string;
  isSimple?: boolean;
  disabled?: boolean;
};

export default function EditorToolbar({ id, isSimple, disabled, ...other }: EditorToolbarProps) {
  const { enqueueSnackbar } = useSnackbar();

  if (linkErrorMessage) {
    enqueueSnackbar(linkErrorMessage, { variant: "error" });
    linkErrorMessage = '';
  };

  return (
    <EditorToolbarStyle {...other} className={disabled ? 'disabled' : ''}>
      <div id={id}>
        {!isSimple && <div className="ql-formats">
          <select className="ql-font" defaultValue="">
            <option value="">Font</option>
            {FONT_FAMILY.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>

          <select className="ql-size" defaultValue="16px">
            {FONT_SIZE.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <select className="ql-header" defaultValue="">
            {HEADINGS.map((heading, index) => (
              <option key={heading} value={index + 1}>
                {heading}
              </option>
            ))}
            <option value="">Normal</option>
          </select>
        </div>}

        <div className="ql-formats">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-underline" />
          {!isSimple && <button type="button" className="ql-strike" />}
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <select className="ql-color" />
            <select className="ql-background" />
          </div>
        )}

        {!isSimple && <div className="ql-formats">
          <button type="button" className="ql-list" value="ordered" />
          <button type="button" className="ql-list" value="bullet" />
        </div>}

        {!isSimple && <div className="ql-formats">
          <button type="button" className="ql-indent" value="-1" />
          <button type="button" className="ql-indent" value="+1" />
        </div>}

        <div className="ql-formats">
          <button type="button" className="ql-script" value="super" />
          <button type="button" className="ql-script" value="sub" />
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <button type="button" className="ql-code-block" />
            <button type="button" className="ql-blockquote" />
          </div>
        )}

        {!isSimple && <div className="ql-formats">
          <button type="button" className="ql-direction" value="rtl" />
          <select className="ql-align" />
        </div>}

        <div className="ql-formats">
          <button type="button" className="ql-link" />
          {!isSimple && <button type="button" className="ql-image" />}
          {!isSimple && <button type="button" className="ql-video" />}
        </div>

        <div className="ql-formats">
          {!isSimple && <button type="button" className="ql-formula" />}
          <button type="button" className="ql-clean" />
        </div>

        {!isSimple && (
          <div className="ql-formats">
            <button type="button" className="ql-undo">
              <IconUndo />
            </button>
            <button type="button" className="ql-redo">
              <IconRedo />
            </button>
          </div>
        )}
      </div>
    </EditorToolbarStyle>
  );
}
